/**
 * AI dubbing service.
 *
 * Sibling of subtitleService.js: where that transcribes a video into subtitles,
 * this generates DUBBED AUDIO tracks — for each requested language, Gemini
 * transcribes + translates the dialogue and Gemini TTS speaks each line, timed to
 * the video (see server dubBuild.js). Reuses the same analyze payload builder so
 * File uploads and library/CDN videos resolve identically, then adds the dub
 * params (target languages + voice).
 *
 * Result: a list of { language, format:'m4a', url } audio tracks the CMS can
 * attach to the content as playable dubbed audio.
 *
 * Requires REACT_APP_GEMINI_ANALYZE_URL to point at the analyze server.
 */

import axios from "axios";
import buildVideoAnalyzePayload, {
  VideoSourceError,
} from "./gemini/buildAnalyzeRequest";

const GEMINI_BASE_URL = process.env.REACT_APP_GEMINI_ANALYZE_URL || "";

export { VideoSourceError };

/** Stages surfaced to the UI while a dub job runs (server `stage` ids map onto these). */
export const DUB_STAGES = [
  { id: "uploading", label: "Uploading" },
  { id: "extracting_audio", label: "Extracting audio" },
  { id: "transcribing", label: "Transcribing" },
  { id: "dubbing", label: "Voicing" },
];

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const requireServer = () => {
  if (!GEMINI_BASE_URL) {
    throw new Error(
      "AI dubbing isn't configured. Set REACT_APP_GEMINI_ANALYZE_URL to the analyze server."
    );
  }
  return GEMINI_BASE_URL.replace(/\/$/, "");
};

/** Attach dub params (target languages + voice) onto the analyze payload. */
const withDubParams = (payload, { targetLanguages, voice }) => {
  const langs = Array.isArray(targetLanguages)
    ? targetLanguages.filter(Boolean).join(",")
    : "";
  if (payload.type === "multipart") {
    if (langs) payload.body.append("target_languages", langs);
    if (voice) payload.body.append("voice", voice);
    return payload;
  }
  return {
    ...payload,
    body: { ...payload.body, target_languages: langs || null, voice: voice || null },
  };
};

const startDubJob = async (payload) => {
  const url = `${requireServer()}/videos/generate-dub`;
  const config =
    payload.type === "multipart"
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  const response = await axios.post(url, payload.body, config);
  const data = response.data?.data ?? response.data;
  return data?.job_id || data?.jobId;
};

const fetchDubStatus = async (jobId) => {
  const url = `${requireServer()}/videos/generate-dub/${jobId}`;
  const response = await axios.get(url);
  return response.data?.data ?? response.data;
};

const pollDubJob = async ({ jobId, onStage, signal }) => {
  const pollMs = 2500;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const status = await fetchDubStatus(jobId);
    if (status?.stage) onStage?.(status.stage, status.progress ?? 0, status.note);

    if (status?.status === "failed") {
      throw new Error(status.error || "Dub generation failed. Please try again.");
    }
    if (status?.status === "complete") {
      onStage?.("complete", 100);
      return status;
    }

    await wait(pollMs, signal);
  }
};

/**
 * Generate dubbed audio tracks for a video.
 *
 * @param {object}   params
 * @param {object}   params.video            File or library/CDN video descriptor.
 * @param {string[]} params.targetLanguages  ISO 639-1 codes to dub into.
 * @param {string}   [params.voice]          Gemini TTS voice name.
 * @param {function} [params.onStage]        (stageId, pct, note) => void progress callback.
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ audioTracks: Array<{language:string,format:string,url:string,voice:string,cuesSpoken:number}>, warnings:string[] }>}
 */
export const generateDub = async ({
  video,
  targetLanguages = [],
  voice,
  onStage,
  signal,
} = {}) => {
  const base = buildVideoAnalyzePayload(video);
  if (!base) throw new Error("No video selected for dubbing.");
  if (!Array.isArray(targetLanguages) || targetLanguages.length === 0) {
    throw new Error("Pick at least one language to dub into.");
  }

  const payload = withDubParams(base, { targetLanguages, voice });

  onStage?.(DUB_STAGES[0].id, 0);

  const jobId = await startDubJob(payload);
  if (!jobId) throw new Error("Dub API did not return a job id.");

  const finished = await pollDubJob({ jobId, onStage, signal });
  const audioTracks = finished.result?.audioTracks;
  if (!Array.isArray(audioTracks) || audioTracks.length === 0) {
    throw new Error("Dub job finished without producing any audio.");
  }

  return { audioTracks, warnings: finished.result?.warnings || [] };
};

export default generateDub;
